import { CellValue } from '@/shared/ui/data/EditableGrid/types/cell.types';
import { Row } from '@/shared/ui/data/EditableGrid/types/row.types';
import { Column } from '@/shared/ui/data/EditableGrid/types/column.types';
import { useCallback, useState } from 'react';
import { GridState } from '@/shared/ui/data/EditableGrid/types/grid.types';
import { Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import { isSamePosition } from '@/shared/ui/data/EditableGrid/lib/position';

interface IOptions<T = Record<string, CellValue>> {
  initialRows: ReadonlyArray<Row<T>>;
  columns: ReadonlyArray<Column>;
  enableDiff: boolean;
}

export function useGridState<T extends Record<string, CellValue>>(options: IOptions<T>) {
  const { initialRows, columns, enableDiff } = options;

  const [gridState, setGridState] = useState<GridState<T>>({
    rows: initialRows,
    columns,
    focusedCell: null,
    selectedCells: [],
    editingCell: null,
    enableDiff,
  });

  const setFocus = useCallback((position: Position | null) => {
    setGridState((prev) => ({
      ...prev,
      focusedCell: position,
    }));
  }, []);

  const setSelection = useCallback((positions: ReadonlyArray<Position>) => {
    setGridState((prev) => ({
      ...prev,
      selectedCells: positions,
    }));
  }, []);

  const setEditingCell = useCallback((position: Position | null) => {
    setGridState((prev) => ({
      ...prev,
      editingCell: position,
    }));
  }, []);

  const updateCellValue = useCallback((position: Position, newValue: CellValue) => {
    setGridState((prev) => {
      const newRows = prev.rows.map((row, rowIndex) => {
        if (rowIndex !== position.rowIndex) return row;

        const columnId = prev.columns[position.colIndex]?.id;
        if (!columnId) return row;

        return {
          ...row,
          data: {
            ...row.data,
            [columnId]: newValue,
          } as T,
        };
      });

      return {
        ...prev,
        rows: newRows,
      };
    });
  }, []);

  const isFocused = useCallback(
    (position: Position): boolean => {
      return gridState.focusedCell != null && isSamePosition(gridState.focusedCell, position);
    },
    [gridState.focusedCell]
  );

  const isEditing = useCallback(
    (position: Position): boolean => {
      return gridState.editingCell != null && isSamePosition(gridState.editingCell, position);
    },
    [gridState.editingCell]
  );

  return {
    gridState,
    setFocus,
    setSelection,
    setEditingCell,
    updateCellValue,
    isFocused,
    isEditing,
  };
}
